import { useMemo, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, MenuItem, Select, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Customer } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { usePermissions } from '../../hooks/usePermissions'

/// Customers admin page — CRUD over the Org → Customer → Site hierarchy
/// layer. Org admins land here; customer_admin doesn't have the
/// permission for create/delete (see customers:create/delete in the
/// permissions catalog).
export function CustomersTab() {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 60_000,
  })
  const customers: Customer[] = Array.isArray(customersQ.data) ? customersQ.data : []

  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<
    { mode: 'create' } | { mode: 'edit'; customer: Customer } | null
  >(null)
  const [confirmDelete, setConfirmDelete] = useState<Customer | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return customers
    return customers.filter((c) =>
      c.name.toLowerCase().includes(q) || (c.slug ?? '').toLowerCase().includes(q),
    )
  }, [customers, search])

  const createM = useMutation({
    mutationFn: (body: { name: string; slug?: string; description?: string; status?: 'active' | 'inactive' }) =>
      api.customers.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); toast.success('Customer created'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const updateM = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; slug?: string; description?: string; status?: 'active' | 'inactive' }) =>
      api.customers.update(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); toast.success('Customer updated'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const deleteM = useMutation({
    mutationFn: (id: string) => api.customers.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); setConfirmDelete(null); toast.success('Customer deleted') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {customers.length} customer{customers.length === 1 ? '' : 's'} in your organization
        </Typography>
        <Tooltip title={can('customers:create') ? '' : "You don't have permission"} arrow>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setDialog({ mode: 'create' })}
              disabled={!can('customers:create')}
              sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}
            >
              New customer
            </Button>
          </Box>
        </Tooltip>
      </Stack>

      <TextField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search customers…"
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 360 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {customersQ.isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{
          py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider',
          borderRadius: 2,
        }}>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {customers.length === 0
              ? 'No customers yet. Create one to start scoping data.'
              : `No customers match "${search}".`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
          {filtered.map((c) => (
            <CustomerCard key={c.id} customer={c}
              canEdit={can('customers:update')}
              canDelete={can('customers:delete')}
              onEdit={() => setDialog({ mode: 'edit', customer: c })}
              onDelete={() => setConfirmDelete(c)} />
          ))}
        </Box>
      )}

      {dialog && (
        <CustomerFormDialog
          mode={dialog.mode}
          customer={dialog.mode === 'edit' ? dialog.customer : undefined}
          onClose={() => setDialog(null)}
          onSubmit={(data) => {
            if (dialog.mode === 'create') createM.mutate(data)
            else updateM.mutate({ id: dialog.customer.id, ...data })
          }}
          loading={createM.isPending || updateM.isPending}
        />
      )}

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => !deleteM.isPending && setConfirmDelete(null)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete {confirmDelete?.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {confirmDelete?.site_count
              ? `This customer still has ${confirmDelete.site_count} site${confirmDelete.site_count === 1 ? '' : 's'} attached. Move or delete them first.`
              : 'This permanently removes the customer. Cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteM.isPending}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={() => confirmDelete && deleteM.mutate(confirmDelete.id)}
            disabled={deleteM.isPending || Boolean(confirmDelete?.site_count)}
          >
            {deleteM.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function CustomerCard({ customer, canEdit, canDelete, onEdit, onDelete }: {
  customer: Customer
  canEdit: boolean
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
      p: 2.25, bgcolor: 'background.paper',
    }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 1.5, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main', flexShrink: 0,
        }}>
          <BusinessOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }} noWrap>
              {customer.name}
            </Typography>
            {customer.status === 'inactive' && (
              <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover' }} />
            )}
          </Stack>
          {customer.description && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.4, lineHeight: 1.45 }} noWrap>
              {customer.description}
            </Typography>
          )}
          {typeof customer.site_count === 'number' && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
              {customer.site_count} site{customer.site_count === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.25}>
          <Tooltip title={canEdit ? 'Edit' : "You don't have permission"} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <IconButton size="small" onClick={onEdit} disabled={!canEdit}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : "You don't have permission"} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <IconButton size="small" onClick={onDelete} disabled={!canDelete}
                sx={{ '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  )
}

function CustomerFormDialog({ mode, customer, onClose, onSubmit, loading }: {
  mode: 'create' | 'edit'
  customer?: Customer
  onClose: () => void
  onSubmit: (data: { name: string; slug?: string; description?: string; status?: 'active' | 'inactive' }) => void
  loading: boolean
}) {
  const [name, setName] = useState(customer?.name ?? '')
  const [slug, setSlug] = useState(customer?.slug ?? '')
  const [description, setDescription] = useState(customer?.description ?? '')
  const [status, setStatus] = useState<'active' | 'inactive'>(customer?.status ?? 'active')

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {mode === 'create' ? 'Create a new customer' : `Edit ${customer?.name}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Customer name" required autoFocus fullWidth size="small"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Manufacturing"
          />
          <TextField
            label="Slug (optional)" fullWidth size="small"
            value={slug} onChange={(e) => setSlug(e.target.value)}
            placeholder="acme-mfg"
            helperText="Short identifier used in URLs and exports. Lowercase, no spaces."
          />
          <TextField
            label="Description (optional)" fullWidth size="small" multiline minRows={2}
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal notes about this customer"
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 80 }}>Status</Typography>
            <Select value={status} size="small" onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
              sx={{ minWidth: 160 }}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || loading}
          onClick={() => onSubmit({
            name: name.trim(),
            slug: slug.trim() || undefined,
            description: description.trim() || undefined,
            status,
          })}
        >
          {loading ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create customer' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
